import { Display } from "rot-js"
import { Color } from "../defines/colors"
import { ItemType, PlayerAction } from "../defines/enums"
import { Collectible, Offset } from "../defines/types"
import { CollectiblesDefaults, EngineDefaults } from "../defines/values"
import { FLOOR_TILE, HILITE_TILE, Tile, WALL_TILE } from "../map/tile"
import { getASCII } from "../utils/collectibles"
import { Popup } from "./popup"

export class Collectibles extends Popup {
  private _fgItem: string
  private _tiles: Tile[][] = []
  private _collectibleType: ItemType
  private _collectibles: Collectible[][]
  private _selectedRow: number
  private _selectedColumn: number

  constructor(
    offsets: Offset,
    name: string,
    fgItem: string,
    collectibleType: ItemType
  ) {
    super(
      EngineDefaults.TROPHY_CASE_WIDTH,
      EngineDefaults.TROPHY_CASE_HEIGHT,
      offsets,
      name
    )
    this._fgItem = fgItem
    this._collectibleType = collectibleType

    this._collectibles = new Array(CollectiblesDefaults.COLLECTIBLES_ROWS)
    for (let i = 0; i < this._collectibles.length; ++i) {
      this._collectibles[i] = new Array(
        CollectiblesDefaults.COLLECTIBLES_COLUMNS
      )
    }

    this.initTiles()
    this.initCubbies()

    this._selectedRow = 0
    this._selectedColumn = 0
    this.highlightSelection()
  }

  private resetGrid(): void {
    this._collectibles = new Array(CollectiblesDefaults.COLLECTIBLES_ROWS)
    for (let i = 0; i < this._collectibles.length; ++i) {
      this._collectibles[i] = new Array(
        CollectiblesDefaults.COLLECTIBLES_COLUMNS
      )
    }
  }

  prepareToRender(): void {
    this.resetGrid()

    const player = window.engine.getPlayer()
    if (player !== undefined) {
      let collectibles = player.getCollectibles(this._collectibleType)

      let row = 0
      let column = 0
      collectibles.forEach((c) => {
        const ascii = getASCII(this._collectibleType, c.name)
        this._collectibles[row][column] = c
        this.placeCollectible(column, row, ascii)
        column += 1
        if (column == CollectiblesDefaults.COLLECTIBLES_COLUMNS) {
          row += 1
          column = 0
        }
      })
    }
  }

  postRender(): void {}

  render(display: Display) {
    this.sendItemViewMessage()

    for (let y = 0; y < this._tiles.length; ++y) {
      const row = this._tiles[y]
      for (let x = 0; x < row.length; ++x) {
        const tile = row[x]
        display.draw(
          x + this.offsets.xOffset,
          y + this.offsets.yOffset,
          tile.light.symbol,
          tile.light.fg,
          tile.light.bg
        )
      }
    }

    window.engine.requestUpdate()
  }

  update(action: PlayerAction) {
    let dX = this._selectedColumn
    let dY = this._selectedRow

    if (action == PlayerAction.MOVE_UP && dY > 0) {
      dY -= 1
    } else if (
      action == PlayerAction.MOVE_DOWN &&
      dY < CollectiblesDefaults.COLLECTIBLES_ROWS - 1
    ) {
      dY += 1
    } else if (action == PlayerAction.MOVE_LEFT && dX > 0) {
      dX -= 1
    } else if (
      action == PlayerAction.MOVE_RIGHT &&
      dX < CollectiblesDefaults.COLLECTIBLES_COLUMNS - 1
    ) {
      dX += 1
    }

    if (dX != this._selectedColumn || dY != this._selectedRow) {
      this.unHighlightSelection()
      this._selectedColumn = dX
      this._selectedRow = dY
      this.highlightSelection()
    }
  }

  private sendItemViewMessage(): void {
    const item = this._collectibles[this._selectedRow][this._selectedColumn]
    if (item != undefined) {
      window.engine.sendMessage(
        `%c{${Color.MESSAGE_ITEM_FG}}%b{${Color.MESSAGE_ITEM_BG}}${item.name}` +
          `%c{${Color.MESSAGE_ANY_FG}}%b{${Color.MESSAGE_ITEM_BG}} (found ${item.found})`,
        false
      )
    } else {
      window.engine.sendMessage(".....empty.....", false)
    }
  }

  private placeCollectible(col: number, row: number, t: string[]) {
    const coords: { x: number; y: number } = this.getCubbyDisplayCoordinates(
      row,
      col
    )

    let y = coords.y
    let x = coords.x
    for (let artY = 0; artY < t.length; ++artY) {
      x = coords.x
      const row = this._tiles[y]
      for (let artX = 0; artX < t[artY].length; ++artX) {
        row[x] = this.replaceTile(t[artY][artX], this._fgItem)
        x += 1
      }
      y += 1
    }
  }

  private getCubbyDisplayCoordinates(
    row: number,
    col: number
  ): { x: number; y: number } {
    const x =
      CollectiblesDefaults.PADDING_LEFT +
      col * CollectiblesDefaults.CUBBIE_WIDTH +
      col * CollectiblesDefaults.CUBBIE_COL_SPACING

    const y =
      CollectiblesDefaults.PADDING_TOP +
      row * CollectiblesDefaults.CUBBIE_HEIGHT +
      row * CollectiblesDefaults.CUBBIE_ROW_SPACING

    return { x: x, y: y }
  }

  private highlightSelection() {
    const startX =
      CollectiblesDefaults.PADDING_LEFT +
      this._selectedColumn * CollectiblesDefaults.CUBBIE_WIDTH +
      this._selectedColumn * CollectiblesDefaults.CUBBIE_COL_SPACING -
      1
    const startY =
      CollectiblesDefaults.PADDING_TOP +
      this._selectedRow * CollectiblesDefaults.CUBBIE_HEIGHT +
      this._selectedRow * CollectiblesDefaults.CUBBIE_ROW_SPACING -
      1

    for (
      let hiliteY = startY;
      hiliteY < startY + CollectiblesDefaults.CUBBIE_HEIGHT + 2;
      ++hiliteY
    ) {
      const row = this._tiles[hiliteY]
      if (
        hiliteY == startY ||
        hiliteY == startY + CollectiblesDefaults.CUBBIE_HEIGHT + 1
      ) {
        for (
          let hiliteX = startX;
          hiliteX < startX + CollectiblesDefaults.CUBBIE_WIDTH + 1;
          ++hiliteX
        ) {
          row[hiliteX] = { ...HILITE_TILE }
        }
      } else {
        row[startX] = { ...HILITE_TILE }
        row[startX + CollectiblesDefaults.CUBBIE_WIDTH] = { ...HILITE_TILE }
      }
    }

    // window.engine.sendMessage("Viewing " + this.name)
  }

  private unHighlightSelection() {
    const startX =
      CollectiblesDefaults.PADDING_LEFT +
      this._selectedColumn * CollectiblesDefaults.CUBBIE_WIDTH +
      this._selectedColumn * CollectiblesDefaults.CUBBIE_COL_SPACING -
      1
    const startY =
      CollectiblesDefaults.PADDING_TOP +
      this._selectedRow * CollectiblesDefaults.CUBBIE_HEIGHT +
      this._selectedRow * CollectiblesDefaults.CUBBIE_ROW_SPACING -
      1

    for (
      let hiliteY = startY;
      hiliteY < startY + CollectiblesDefaults.CUBBIE_HEIGHT + 2;
      ++hiliteY
    ) {
      const row = this._tiles[hiliteY]
      if (
        hiliteY == startY ||
        hiliteY == startY + CollectiblesDefaults.CUBBIE_HEIGHT + 1
      ) {
        for (
          let hiliteX = startX;
          hiliteX < startX + CollectiblesDefaults.CUBBIE_WIDTH + 1;
          ++hiliteX
        ) {
          row[hiliteX] = { ...FLOOR_TILE }
        }
      } else {
        row[startX] = { ...FLOOR_TILE }
        row[startX + CollectiblesDefaults.CUBBIE_WIDTH] = { ...FLOOR_TILE }
      }
    }
  }

  private initCubbies() {
    let spaceX = 0
    let spaceY = 0

    for (
      let y = CollectiblesDefaults.PADDING_TOP;
      y < this._tiles.length - CollectiblesDefaults.PADDING_TOP;
      ++y
    ) {
      spaceY += 1
      spaceX = 0
      for (
        let x = CollectiblesDefaults.PADDING_LEFT;
        x < this._tiles[y].length - CollectiblesDefaults.PADDING_LEFT;
        ++x
      ) {
        spaceX += 1
        if (spaceX < CollectiblesDefaults.CUBBIE_WIDTH) {
          this._tiles[y][x] = { ...WALL_TILE }
        } else if (
          spaceX ==
          CollectiblesDefaults.CUBBIE_WIDTH +
            CollectiblesDefaults.CUBBIE_COL_SPACING
        ) {
          spaceX = 0
        }
      }

      if (spaceY == CollectiblesDefaults.CUBBIE_HEIGHT) {
        spaceY = 0
        y += CollectiblesDefaults.CUBBIE_ROW_SPACING
      }
    }
  }

  protected initTiles() {
    this._tiles = new Array(this.height)
    for (let y = 0; y < this.height; ++y) {
      const row = new Array(this.width)
      for (let x = 0; x < this.width; ++x) {
        const isWall =
          x === 0 || x === this.width - 1 || y === 0 || y === this.height - 1
        row[x] = isWall ? { ...WALL_TILE } : { ...FLOOR_TILE }
      }
      this._tiles[y] = row
    }
  }

  private replaceTile(symbol: string, fg: string) {
    return {
      walkable: false,
      target: null,
      light: {
        symbol: symbol,
        fg: fg,
        bg: Color.WALL_TILE_DARK_BG,
      },
      dark: {
        symbol: symbol,
        fg: Color.WALL_TILE_DARK_FG,
        bg: Color.WALL_TILE_DARK_BG,
      },
      transparent: true,
      visible: true,
      seen: true,
    }
  }
}
